import { ɵɵCdkFixedSizeVirtualScroll, ɵɵCdkVirtualForOf, ɵɵCdkVirtualScrollViewport } from '@angular/cdk/overlay';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, inject, input, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { ModalService } from '@src/suite/common/components/modal/modal.service';
import { CardViewType, SearchType } from '@src/suite/common/enums/enums';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { SidecarEfffectActions, SidecarMenu } from '@src/suite/common/store/fullscreen';
import { FullscreenActions } from '@src/suite/common/store/fullscreen';
import { selectItemLayoutState } from '@src/suite/common/store/fullscreen/selectors/fullscreen.selectors';
import {
    selectSavedStoryViewStates,
    selectSidecarCurrentStory,
    selectSidecarCurrentStoryRelatives,
    selectSidecarCurrentStoryRelativesIndex,
    selectSidecarCurrentStoryVS,
    selectSidecarSelectedStoryIndex,
    selectSidecarSelectedStoryListSearchType,
    selectSidecarStories,
    selectSidecarTotalStoryCount,
} from '@src/suite/common/store/fullscreen/selectors/sidecar.selectors';
import { selectFieldDefsDict } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectTopicRecordsMap } from '@src/suite/common/store/records/records.selectors';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { getCardViewTypeForWidth, isStoryApproveField } from '@src/suite/common/utils/util';

import { ClickStopPropagationDirective } from '../../directives/stop-propagation';
import { StoryFullScreenComponent } from '../../st-full-screen-dialog/stories-full-screen/story-full-screen/story-full-screen';
import { StCondensedListHeaderLabelsComponent } from '../condensed-list-labels/st-condensed-list-header-labels';
import { StItemLayoutToggleComponent } from '../item-layout-toggle/st-item-layout-toggle';
import { SimpleSearchBoxAutocompleteComponent } from '../search-box/simple-search-box-autocomplete/simple-search-box-autocomplete';
import { StoryItemComponent } from '../stories-list/story-item/story-item';
import { StStoryItemCondensedComponent } from '../stories-list/story-item-condensed/st-story-item-condensed';

@Component({
    selector: 'st-stories-list-sidecar',
    imports: [
        ClickStopPropagationDirective,
        MatButtonToggle,
        MatButtonToggleGroup,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatLabel,
        MatOption,
        MatSelect,
        ɵɵCdkFixedSizeVirtualScroll,
        ɵɵCdkVirtualForOf,
        ɵɵCdkVirtualScrollViewport,
        SimpleSearchBoxAutocompleteComponent,
        StCondensedListHeaderLabelsComponent,
        StItemLayoutToggleComponent,
        StoryFullScreenComponent,
        StoryItemComponent,
        StStoryItemCondensedComponent,
    ],
    templateUrl: './stories-list-sidecar.html',
    styleUrl: './stories-list-sidecar.scss',
    providers: [{ provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: { hideSingleSelectionIndicator: false } }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-stories-list-sidecar',
    },
})
export class StoriesListSidecarDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    modalService = inject(ModalService);
    store = inject(Store);
    dialogRef = inject(MatDialogRef<StoriesListSidecarDialogComponent>);
    elmRef = inject(ElementRef);

    SearchType = SearchType;
    CardViewType = CardViewType;
    NavigationDirection = NavigationDirection;
    private containerResizeObserver: ResizeObserver;

    readonly languageSettings = this.store.selectSignal(selectI18N)();
    readonly storiesLabel = this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebStoriesLabel));
    readonly itemLayoutState = this.store.selectSignal(selectItemLayoutState);

    public readonly searchTerm = signal('');
    private readonly currentWidth = signal(0);

    readonly selectedListSearchTypeInternal = computed(() => this.store.selectSignal(selectSidecarSelectedStoryListSearchType(this.sidecarMenu()))());
    readonly savedViewDropdownVisible = computed(() => this.selectedListSearchType() === SearchType.SAVED);
    readonly selectStories = computed(() => this.store.selectSignal(selectSidecarStories(this.sidecarMenu()))());
    readonly selectSavedViewStates = computed(() => this.store.selectSignal(selectSavedStoryViewStates(this.sidecarMenu()))());
    readonly currentViewState = computed(() => this.store.selectSignal(selectSidecarCurrentStoryVS(this.sidecarMenu()))());
    readonly currentSelectedStory = computed(() => this.store.selectSignal(selectSidecarCurrentStory(this.sidecarMenu()))());
    readonly currentSelectedStoryRelatives = computed(() => this.store.selectSignal(selectSidecarCurrentStoryRelatives(this.sidecarMenu()))());

    readonly currentIdx = computed(() => {
        if (this.showStoriesNavigation()) {
            return this.store.selectSignal(selectSidecarCurrentStoryRelativesIndex(this.sidecarMenu()))() + 1;
        } else {
            const indexInParent = this.store.selectSignal(selectSidecarSelectedStoryIndex(this.sidecarMenu()))();
            if (indexInParent == null) {
                return 0;
            }
            return indexInParent + 1;
        }
    });

    readonly showStoriesNavigation = computed(() => {
        return this.currentSelectedStory()?.PSTPrimaryStoryGUID ? true : false;
    });

    readonly totalCount = computed(() => {
        if (this.showStoriesNavigation()) {
            return this.currentSelectedStoryRelatives().length;
        } else {
            return this.store.selectSignal(selectSidecarTotalStoryCount(this.sidecarMenu()))();
        }
    });
    readonly isSideCarMenuSelected = computed(() => {
        return this.sidecarMenu();
    });
    readonly isStoriesCondensedLayout = computed(() => this.itemLayoutState().storiesSidecar);
    readonly showApproveColumn = computed(() => isStoryApproveField(this.store.selectSignal(selectFieldDefsDict)()?.[PSTFieldNames.Approved] as FieldDef));

    readonly parentTopic = computed(() => {
        if (this.currentSelectedStory()?.PSTStoryTopicGUID) {
            const allTopics = this.store.selectSignal(selectTopicRecordsMap)();
            return allTopics?.get(this.currentSelectedStory().PSTStoryTopicGUID);
        }
        return null;
    });

    readonly selectedListSearchType = computed(() => {
        const selectedListSearchType = this.selectedListSearchTypeInternal();
        if (!selectedListSearchType) {
            return SearchType.SAVED;
        }
        return selectedListSearchType;
    });

    readonly itemSize = computed(() => {
        if (this.isStoriesCondensedLayout()) {
            return 50;
        }
        if (this.cardViewType() === CardViewType.SMALL) {
            return 142;
        } else {
            return 138;
        }
    });

    readonly cardViewType = computed(() => getCardViewTypeForWidth(this.currentWidth(), this.isStoriesCondensedLayout()));

    readonly sidecarMenu = input<FullscreenLayoutAreas>();

    ngOnInit() {
        if (!this.selectSavedViewStates()) {
            this.store.dispatch(SidecarEfffectActions.loadSidecarStoryViewStates({ sidecarType: this.sidecarMenu() }));
        }
    }

    ngAfterViewInit(): void {
        this.containerResizeObserver = new ResizeObserver(entries => {
            const width = entries?.[0]?.contentRect?.width;
            if (width) {
                this.currentWidth.set(width);
            }
        });

        this.containerResizeObserver?.observe(this.elmRef?.nativeElement);
    }

    ngOnDestroy(): void {
        this.containerResizeObserver?.unobserve(this.elmRef?.nativeElement);
        this.containerResizeObserver?.disconnect();
    }

    public async toggleSearchTab(event: MatButtonToggleChange) {
        this.store.dispatch(SidecarEfffectActions.setStoriesListSearchType({ searchType: event.value, sidecarType: this.sidecarMenu() }));
        if (this.selectedListSearchType() === SearchType.SEARCH) {
            this.store.dispatch(SidecarEfffectActions.clearSearchStory({ sidecarType: this.sidecarMenu() }));
        }
        if (this.savedViewDropdownVisible()) {
            // if we are switching from search to saved views, and there is a saved view selected
            // we need to prefetch again
            this.searchTerm.set('');
            this.store.dispatch(SidecarEfffectActions.reloadSidecarStoryBasedOnCurrentViewState({ sidecarType: this.sidecarMenu() }));
        }
    }

    public onSearchHandler(value: string | number) {
        this.store.dispatch(SidecarEfffectActions.searchStories({ searchTerm: value.toString(), sidecarType: this.sidecarMenu() }));
    }

    public cancelNew() {
        this.dialogRef.close();
    }

    public trackByGuid(_index, node) {
        if (node) {
            return node?.Guid;
        }
    }

    onViewStateGuidChanged(viewState: MatSelectChange) {
        this.store.dispatch(SidecarEfffectActions.setSidecarStoryCurrentViewState({ guid: viewState.value, sidecarType: this.sidecarMenu() }));
    }

    onFullscreenHandler(data: StorytellingRecord) {
        this.store.dispatch(SidecarEfffectActions.openStoryInSidecar({ guid: data.Guid, sidecarType: this.sidecarMenu(), sidecarMenu: SidecarMenu.STORIES }));
    }

    setStoriesCondensedLayout(isCondensed: boolean): void {
        this.store.dispatch(FullscreenActions.setItemLayoutState({ state: { storiesSidecar: isCondensed } }));
    }

    public navigate(_event, direction: NavigationDirection) {
        this.store.dispatch(SidecarEfffectActions.navigateStory({ direction, sidecarMenu: this.sidecarMenu(), throughTopicStories: this.showStoriesNavigation() }));
    }

    @HostListener('document:keyup', ['$event'])
    public navigateForward(event) {
        if (
            event &&
            event.target &&
            event.target.tagName &&
            !(
                event.target.tagName.toLowerCase() === 'input' ||
                event.target.tagName.toLowerCase() === 'textarea' ||
                event.target.classList.contains('fr-element') ||
                event.target.classList.contains('fr-view')
            )
        ) {
            if (event.code === 'Period') {
                this.navigate(event, NavigationDirection.NEXT);
            } else if (event.code === 'Comma') {
                this.navigate(event, NavigationDirection.PREVIOUS);
            }
        }
    }
}
